import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@material-ui/core';

import { ReservationDetails, VipCategoryType } from '../../types';
import { updateVip, useMutateApiData } from '../../util/api';
import { ErrorMessage } from '../common';
import { SelectField } from '../common/section';
import SwitchField from '../common/section/SwitchField';

interface VipSectionProps {
  reservation: ReservationDetails['reservation'];
  refetchReservation: () => Promise<unknown>;
}

const VIP_CATEGORY_OPTIONS = [
  { label: '—', value: '' },
  { label: 'Investor', value: VipCategoryType.INVESTOR },
  { label: 'Landlord', value: VipCategoryType.LANDLORD },
  { label: 'Journalist', value: VipCategoryType.JOURNALIST },
  { label: 'Influencer', value: VipCategoryType.INFLUENCER },
  { label: 'Super', value: VipCategoryType.SUPER },
];

const VipSection: React.FC<VipSectionProps> = ({ reservation, refetchReservation }) => {
  const { t } = useTranslation();

  const { error, isLoading, mutateAsync: updateVipRequest } = useMutateApiData(updateVip);
  const [isVip, setIsVip] = React.useState(Boolean(reservation.isVip));
  const [vipCategoryType, setVipCategoryType] = React.useState(reservation.vipCategoryType || '');

  React.useEffect(() => {
    setIsVip(Boolean(reservation.isVip));
    setVipCategoryType(reservation.vipCategoryType || '');
  }, [reservation.isVip, reservation.vipCategoryType]);

  const onVipChange = (_field: string, value: boolean) => {
    setIsVip(value);
    if (!value) {
      setVipCategoryType('');
    }
  };

  const onCategoryChange = (_field: string, value: string) => {
    setVipCategoryType(value);
  };

  const onSubmit = async (): Promise<void> => {
    await updateVipRequest([
      reservation.id,
      {
        isVip,
        vipCategoryType: isVip && vipCategoryType ? vipCategoryType : null,
      },
    ]);
    await refetchReservation();
  };

  const showSave = isVip || isVip !== Boolean(reservation.isVip);

  return (
    <>
      <SwitchField field="isVip" label={t('vip.toggle')} onChange={onVipChange} value={isVip} />
      {isVip && (
        <SelectField
          field="vipCategoryType"
          label={t('vip.category')}
          onChange={onCategoryChange}
          value={vipCategoryType}
          options={VIP_CATEGORY_OPTIONS}
          inputLabel={t('vip.category')}
        />
      )}

      {showSave && (
        <Box flexDirection="row">
          <Button variant="outlined" color="primary" onClick={onSubmit} disabled={isLoading}>
            {t('general.save')}
          </Button>

          {isLoading && <Typography>{t('general.loading')}</Typography>}
        </Box>
      )}

      {error && <ErrorMessage message={t('general.error')} />}
    </>
  );
};

export default VipSection;
